import { X, Crown, Star, Lock, Zap } from 'lucide-react'
import { useEffect, useRef } from 'react'

interface PremiumUpsellModalProps {
  isOpen: boolean
  onClose: () => void
  onUpgrade: () => void
  reason?: string
  signatureColor: string
}

export function PremiumUpsellModal({ isOpen, onClose, onUpgrade, reason, signatureColor }: PremiumUpsellModalProps) {
  const modalRef = useRef<HTMLDivElement>(null)
  const firstFocusableRef = useRef<HTMLButtonElement>(null)
  const lastFocusableRef = useRef<HTMLButtonElement>(null)

  // Helper to convert hex to rgba
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  // Handle Escape key to close
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  // Focus trap implementation
  useEffect(() => {
    if (!isOpen) return

    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return

      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )

      if (!focusableElements || focusableElements.length === 0) return

      const firstElement = focusableElements[0] as HTMLElement
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

      if (event.shiftKey) {
        // Shift + Tab: going backwards
        if (document.activeElement === firstElement) {
          event.preventDefault()
          lastElement.focus()
        }
      } else {
        // Tab: going forwards
        if (document.activeElement === lastElement) {
          event.preventDefault()
          firstElement.focus()
        }
      }
    }

    window.addEventListener('keydown', handleTab)

    // Focus first element when modal opens
    setTimeout(() => {
      firstFocusableRef.current?.focus()
    }, 100)

    return () => window.removeEventListener('keydown', handleTab)
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 animate-scaleIn"
      >
        {/* Close button */}
        <button
          ref={firstFocusableRef}
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Close modal"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        {/* Lock icon header */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#C5A572] to-[#B59562] flex items-center justify-center">
            <Lock className="w-8 h-8 text-white" />
          </div>
        </div>

        {/* Title */}
        <h2
          id="modal-title"
          className="text-3xl font-bold text-center mb-3 uppercase tracking-wide"
          style={{ fontFamily: 'Bebas Neue, sans-serif', color: signatureColor }}
        >
          This Challenge is Locked
        </h2>

        {/* Message */}
        <p
          className="text-center text-gray-600 mb-6 text-base"
          style={{ fontFamily: 'Oswald, sans-serif' }}
        >
          {reason || 'Upgrade to Premium for unlimited archive access'}
        </p>

        {/* Premium benefits */}
        <div className="space-y-4 mb-8">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5"
                 style={{ backgroundColor: hexToRgba(signatureColor, 0.1) }}>
              <Crown className="w-4 h-4 text-[#C5A572]" />
            </div>
            <div>
              <p className="font-semibold text-gray-800" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Unlimited Archive Access
              </p>
              <p className="text-sm text-gray-600">
                Play any past challenge from the entire archive
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5"
                 style={{ backgroundColor: hexToRgba(signatureColor, 0.1) }}>
              <Star className="w-4 h-4 text-[#C5A572]" />
            </div>
            <div>
              <p className="font-semibold text-gray-800" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Exclusive Features
              </p>
              <p className="text-sm text-gray-600">
                Access to premium-only games and challenges
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5"
                 style={{ backgroundColor: hexToRgba(signatureColor, 0.1) }}>
              <Zap className="w-4 h-4 text-[#C5A572]" />
            </div>
            <div>
              <p className="font-semibold text-gray-800" style={{ fontFamily: 'Oswald, sans-serif' }}>
                Ad-Free Experience
              </p>
              <p className="text-sm text-gray-600">
                Enjoy uninterrupted gameplay with no advertisements
              </p>
            </div>
          </div>
        </div>

        {/* CTAs */}
        <div className="flex flex-col gap-3">
          {/* Primary CTA */}
          <button
            onClick={onUpgrade}
            className="w-full bg-gradient-to-br from-[#C5A572] to-[#B59562] text-white px-6 py-4 rounded-xl font-bold uppercase tracking-wide transition-all hover:-translate-y-1 hover:shadow-xl"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            Upgrade to Premium
          </button>

          {/* Secondary CTA */}
          <button
            ref={lastFocusableRef}
            onClick={onClose}
            className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-xl font-medium uppercase tracking-wide transition-colors hover:bg-gray-200"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  )
}
